import React, { useEffect, useState, useCallback } from "react";
import api from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import {
  PageContainer, PageHeader, Card, CardHeader,
  StatCard, TabGroup, Badge, Button,
  Table, Thead, Th, Td, TRow,
  FormGroup, Input, Select,
  EmptyState, Muted,
} from "../ui";

// ============================================================
// TYPES
// ============================================================

interface Equipment {
  id: string;
  name: string;
  equipment_type: "tap_line" | "slushie_machine";
  location: string;
  model: string;
  serial_number: string;
  notes: string;
  overdue_count: number;
  last_maintenance_at: string | null;
  created_at: string;
}

interface Schedule {
  id: string;
  equipment_id: string;
  task_name: string;
  description: string;
  procedure_steps: string[];
  frequency_days: number;
  priority: string;
  status: "overdue" | "due_soon" | "ok" | "never_done";
  last_completed_at: string | null;
  next_due_at: string | null;
}

interface LogEntry {
  id: string;
  equipment_id: string;
  equipment_name: string;
  schedule_id: string | null;
  task_name: string;
  work_performed: string;
  parts_used: string;
  parts_cost: number;
  issues_found: string;
  duration_minutes: number | null;
  performed_by: string;
  created_at: string;
}

interface SummaryData {
  overdue: Array<{
    equipment_id: string;
    equipment_name: string;
    schedule_id: string;
    task_name: string;
    days_overdue: number;
  }>;
  due_soon: Array<{
    equipment_id: string;
    equipment_name: string;
    schedule_id: string;
    task_name: string;
    days_until_due: number;
  }>;
  parts_cost_30d: number;
}

// ============================================================
// MAIN PAGE
// ============================================================

const TABS = [
  { key: "dashboard", label: "Dashboard" },
  { key: "equipment", label: "Equipment" },
  { key: "log", label: "Log Maintenance" },
  { key: "history", label: "History" },
];

export default function MaintenancePage() {
  const [activeTab, setActiveTab] = useState("dashboard");

  return (
    <PageContainer>
      <PageHeader
        title="Equipment & Maintenance"
        subtitle="Track cleaning and maintenance for tap lines and slushie machines"
      />
      <TabGroup tabs={TABS} active={activeTab} onChange={setActiveTab} />

      {activeTab === "dashboard" && <DashboardTab />}
      {activeTab === "equipment" && <EquipmentTab />}
      {activeTab === "log" && <LogMaintenanceTab />}
      {activeTab === "history" && <HistoryTab />}
    </PageContainer>
  );
}

// ============================================================
// DASHBOARD TAB
// ============================================================

function DashboardTab() {
  const [summary, setSummary] = useState<SummaryData | null>(null);
  const [loading, setLoading] = useState(true);

  const loadSummary = useCallback(async () => {
    try {
      const res = await api.get("/maintenance/summary");
      setSummary(res.data);
    } catch (err) {
      console.error("Failed to load maintenance summary", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const handleCompleteNow = async (item: SummaryData["overdue"][0]) => {
    try {
      await api.post("/maintenance/log", {
        equipment_id: item.equipment_id,
        schedule_id: item.schedule_id,
        task_name: item.task_name,
        work_performed: "Completed (quick log from dashboard)",
      });
      loadSummary();
    } catch (err) {
      console.error("Failed to log maintenance", err);
    }
  };

  if (loading) {
    return <EmptyState message="Loading dashboard..." icon="" />;
  }

  if (!summary) {
    return <EmptyState message="Unable to load maintenance data" icon="" />;
  }

  return (
    <div className="space-y-6">
      {/* Stat Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard
          label="Overdue"
          value={String(summary.overdue.length)}
          detail="Tasks past due date"
          variant={summary.overdue.length > 0 ? "danger" : "green"}
        />
        <StatCard
          label="Due Soon"
          value={String(summary.due_soon.length)}
          detail="Tasks due within 7 days"
          variant={summary.due_soon.length > 0 ? "gold" : "green"}
        />
        <StatCard
          label="Parts Cost (30 days)"
          value={`$${summary.parts_cost_30d.toFixed(2)}`}
          detail="Maintenance parts expense"
          variant="blue"
        />
      </div>

      {/* Overdue Items */}
      <Card>
        <CardHeader title="Overdue Maintenance" />
        {summary.overdue.length === 0 ? (
          <EmptyState message="No overdue maintenance" detail="Everything is on schedule" icon="" />
        ) : (
          <div className="space-y-3">
            {summary.overdue.map((item) => (
              <div
                key={`${item.equipment_id}-${item.schedule_id}`}
                className="flex items-center justify-between p-4 rounded-xl"
                style={{ background: "var(--rbw-bg)", border: "1px solid var(--rbw-border)" }}
              >
                <div className="flex items-center gap-3">
                  <Badge variant="danger">{item.days_overdue}d overdue</Badge>
                  <div>
                    <div className="font-semibold text-sm" style={{ color: "var(--rbw-navy)" }}>
                      {item.equipment_name}
                    </div>
                    <div className="text-xs" style={{ color: "var(--rbw-gray-dark)" }}>
                      {item.task_name}
                    </div>
                  </div>
                </div>
                <Button size="sm" variant="danger" onClick={() => handleCompleteNow(item)}>
                  Complete Now
                </Button>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Due Soon Items */}
      <Card>
        <CardHeader title="Due Soon" />
        {summary.due_soon.length === 0 ? (
          <EmptyState message="Nothing due soon" detail="No upcoming maintenance in the next 7 days" icon="" />
        ) : (
          <div className="space-y-3">
            {summary.due_soon.map((item) => (
              <div
                key={`${item.equipment_id}-${item.schedule_id}`}
                className="flex items-center justify-between p-4 rounded-xl"
                style={{ background: "var(--rbw-bg)", border: "1px solid var(--rbw-border)" }}
              >
                <div className="flex items-center gap-3">
                  <Badge variant="gold">{item.days_until_due}d until due</Badge>
                  <div>
                    <div className="font-semibold text-sm" style={{ color: "var(--rbw-navy)" }}>
                      {item.equipment_name}
                    </div>
                    <div className="text-xs" style={{ color: "var(--rbw-gray-dark)" }}>
                      {item.task_name}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

// ============================================================
// EQUIPMENT TAB
// ============================================================

function EquipmentTab() {
  const { user } = useAuth();
  const isManager = user?.role === "owner" || user?.role === "manager";

  const [equipment, setEquipment] = useState<Equipment[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [selectedEquipment, setSelectedEquipment] = useState<Equipment | null>(null);
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [equipmentHistory, setEquipmentHistory] = useState<LogEntry[]>([]);
  const [showScheduleForm, setShowScheduleForm] = useState(false);

  const [form, setForm] = useState({
    name: "",
    equipment_type: "tap_line" as "tap_line" | "slushie_machine",
    location: "",
    model: "",
    serial_number: "",
    notes: "",
  });

  const [scheduleForm, setScheduleForm] = useState({
    task_name: "",
    description: "",
    procedure_steps: "",
    frequency_days: "",
    priority: "medium",
  });

  const loadEquipment = useCallback(async () => {
    try {
      const [tapRes, slushieRes] = await Promise.all([
        api.get("/maintenance/equipment", { params: { type: "tap_line" } }),
        api.get("/maintenance/equipment", { params: { type: "slushie_machine" } }),
      ]);
      setEquipment([...tapRes.data, ...slushieRes.data]);
    } catch (err) {
      console.error("Failed to load equipment", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEquipment();
  }, [loadEquipment]);

  const handleSelectEquipment = async (eq: Equipment) => {
    setSelectedEquipment(eq);
    try {
      const [schedRes, histRes] = await Promise.all([
        api.get("/maintenance/schedules", { params: { equipment_id: eq.id } }),
        api.get("/maintenance/log", { params: { equipment_id: eq.id } }),
      ]);
      setSchedules(schedRes.data);
      setEquipmentHistory(histRes.data);
    } catch (err) {
      console.error("Failed to load equipment details", err);
    }
  };

  const handleSubmitEquipment = async () => {
    try {
      if (editingId) {
        await api.put(`/maintenance/equipment/${editingId}`, form);
      } else {
        await api.post("/maintenance/equipment", form);
      }
      setShowForm(false);
      setEditingId(null);
      setForm({ name: "", equipment_type: "tap_line", location: "", model: "", serial_number: "", notes: "" });
      loadEquipment();
    } catch (err) {
      console.error("Failed to save equipment", err);
    }
  };

  const handleEditEquipment = (eq: Equipment) => {
    setForm({
      name: eq.name,
      equipment_type: eq.equipment_type,
      location: eq.location,
      model: eq.model,
      serial_number: eq.serial_number,
      notes: eq.notes,
    });
    setEditingId(eq.id);
    setShowForm(true);
  };

  const handleSubmitSchedule = async () => {
    if (!selectedEquipment) return;
    try {
      const steps = scheduleForm.procedure_steps
        .split("\n")
        .map((s) => s.trim())
        .filter((s) => s.length > 0);
      await api.post("/maintenance/schedules", {
        equipment_id: selectedEquipment.id,
        task_name: scheduleForm.task_name,
        description: scheduleForm.description,
        procedure_steps: steps,
        frequency_days: parseInt(scheduleForm.frequency_days) || 30,
        priority: scheduleForm.priority,
      });
      setShowScheduleForm(false);
      setScheduleForm({ task_name: "", description: "", procedure_steps: "", frequency_days: "", priority: "medium" });
      handleSelectEquipment(selectedEquipment);
    } catch (err) {
      console.error("Failed to create schedule", err);
    }
  };

  if (loading) {
    return <EmptyState message="Loading equipment..." icon="" />;
  }

  // Detail view for selected equipment
  if (selectedEquipment) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" size="sm" onClick={() => setSelectedEquipment(null)}>
          &larr; Back to Equipment
        </Button>

        <Card>
          <CardHeader
            title={selectedEquipment.name}
            action={
              isManager ? (
                <Button size="sm" variant="secondary" onClick={() => handleEditEquipment(selectedEquipment)}>
                  Edit
                </Button>
              ) : undefined
            }
          />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-6">
            <div>
              <Muted>Type</Muted>
              <div className="font-medium" style={{ color: "var(--rbw-navy)" }}>
                {selectedEquipment.equipment_type === "tap_line" ? "Tap Line" : "Slushie Machine"}
              </div>
            </div>
            <div>
              <Muted>Location</Muted>
              <div className="font-medium" style={{ color: "var(--rbw-navy)" }}>
                {selectedEquipment.location || "-"}
              </div>
            </div>
            <div>
              <Muted>Model</Muted>
              <div className="font-medium" style={{ color: "var(--rbw-navy)" }}>
                {selectedEquipment.model || "-"}
              </div>
            </div>
            <div>
              <Muted>Serial #</Muted>
              <div className="font-medium" style={{ color: "var(--rbw-navy)" }}>
                {selectedEquipment.serial_number || "-"}
              </div>
            </div>
          </div>
          {selectedEquipment.notes && (
            <div className="text-sm" style={{ color: "var(--rbw-gray-dark)" }}>
              {selectedEquipment.notes}
            </div>
          )}
        </Card>

        {/* Schedules */}
        <Card>
          <CardHeader
            title="Maintenance Schedules"
            action={
              isManager ? (
                <Button size="sm" onClick={() => setShowScheduleForm(true)}>
                  Add Schedule
                </Button>
              ) : undefined
            }
          />
          {schedules.length === 0 ? (
            <EmptyState message="No schedules set up" detail="Add a maintenance schedule to track tasks" />
          ) : (
            <div className="space-y-3">
              {schedules.map((sched) => (
                <div
                  key={sched.id}
                  className="p-4 rounded-xl"
                  style={{ background: "var(--rbw-bg)", border: "1px solid var(--rbw-border)" }}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-semibold text-sm" style={{ color: "var(--rbw-navy)" }}>
                      {sched.task_name}
                    </div>
                    <Badge
                      variant={
                        sched.status === "overdue"
                          ? "danger"
                          : sched.status === "due_soon"
                          ? "gold"
                          : sched.status === "never_done"
                          ? "default"
                          : "green"
                      }
                    >
                      {sched.status === "overdue"
                        ? "Overdue"
                        : sched.status === "due_soon"
                        ? "Due Soon"
                        : sched.status === "never_done"
                        ? "Never Done"
                        : "OK"}
                    </Badge>
                  </div>
                  {sched.description && (
                    <div className="text-xs mb-2" style={{ color: "var(--rbw-gray-dark)" }}>
                      {sched.description}
                    </div>
                  )}
                  <div className="flex gap-4 text-xs" style={{ color: "var(--rbw-gray-dark)" }}>
                    <span>Every {sched.frequency_days} days</span>
                    <span>Priority: {sched.priority}</span>
                    {sched.last_completed_at && (
                      <span>Last done: {new Date(sched.last_completed_at).toLocaleDateString()}</span>
                    )}
                    {sched.next_due_at && (
                      <span>Next due: {new Date(sched.next_due_at).toLocaleDateString()}</span>
                    )}
                  </div>
                  {sched.procedure_steps && sched.procedure_steps.length > 0 && (
                    <div className="mt-3 pt-3" style={{ borderTop: "1px solid var(--rbw-border)" }}>
                      <div className="text-xs font-semibold mb-1" style={{ color: "var(--rbw-navy)" }}>
                        Procedure:
                      </div>
                      <ol className="list-decimal list-inside text-xs space-y-0.5" style={{ color: "var(--rbw-gray-dark)" }}>
                        {sched.procedure_steps.map((step, i) => (
                          <li key={i}>{step}</li>
                        ))}
                      </ol>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Schedule Form Modal */}
        {showScheduleForm && (
          <Card>
            <CardHeader title="Add Maintenance Schedule" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormGroup label="Task Name">
                <Input
                  value={scheduleForm.task_name}
                  onChange={(v) => setScheduleForm({ ...scheduleForm, task_name: v })}
                  placeholder="e.g. Line Cleaning"
                  required
                />
              </FormGroup>
              <FormGroup label="Frequency (days)">
                <Input
                  value={scheduleForm.frequency_days}
                  onChange={(v) => setScheduleForm({ ...scheduleForm, frequency_days: v })}
                  type="number"
                  placeholder="30"
                  min="1"
                />
              </FormGroup>
              <FormGroup label="Priority">
                <Select
                  value={scheduleForm.priority}
                  onChange={(v) => setScheduleForm({ ...scheduleForm, priority: v })}
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </Select>
              </FormGroup>
              <FormGroup label="Description">
                <Input
                  value={scheduleForm.description}
                  onChange={(v) => setScheduleForm({ ...scheduleForm, description: v })}
                  placeholder="Brief description"
                />
              </FormGroup>
              <FormGroup label="Procedure Steps (one per line)" span={2}>
                <textarea
                  value={scheduleForm.procedure_steps}
                  onChange={(e) => setScheduleForm({ ...scheduleForm, procedure_steps: e.target.value })}
                  placeholder={"Step 1: Disconnect keg\nStep 2: Fill with cleaning solution\nStep 3: Run through lines"}
                  rows={4}
                  className="w-full px-4 py-2.5 rounded-xl outline-none transition-all"
                  style={{
                    border: "2px solid var(--rbw-border)",
                    background: "var(--rbw-bg-card)",
                    color: "var(--rbw-navy)",
                  }}
                  onFocus={(e) => (e.target.style.borderColor = "var(--rbw-blue)")}
                  onBlur={(e) => (e.target.style.borderColor = "var(--rbw-border)")}
                />
              </FormGroup>
            </div>
            <div className="flex gap-3 mt-4">
              <Button onClick={handleSubmitSchedule}>Save Schedule</Button>
              <Button variant="ghost" onClick={() => setShowScheduleForm(false)}>Cancel</Button>
            </div>
          </Card>
        )}

        {/* Equipment History */}
        <Card>
          <CardHeader title="Maintenance History" />
          {equipmentHistory.length === 0 ? (
            <EmptyState message="No maintenance logged yet" />
          ) : (
            <Table>
              <Thead>
                <Th>Date</Th>
                <Th>Task</Th>
                <Th>Work Performed</Th>
                <Th>Parts Cost</Th>
                <Th>Issues</Th>
              </Thead>
              <tbody>
                {equipmentHistory.map((entry, i) => (
                  <TRow key={entry.id} index={i}>
                    <Td>{new Date(entry.created_at).toLocaleDateString()}</Td>
                    <Td bold>{entry.task_name}</Td>
                    <Td>{entry.work_performed}</Td>
                    <Td>{entry.parts_cost ? `$${entry.parts_cost.toFixed(2)}` : "-"}</Td>
                    <Td>{entry.issues_found || "-"}</Td>
                  </TRow>
                ))}
              </tbody>
            </Table>
          )}
        </Card>
      </div>
    );
  }

  // Equipment grid
  const tapLines = equipment.filter((e) => e.equipment_type === "tap_line");
  const slushieMachines = equipment.filter((e) => e.equipment_type === "slushie_machine");

  return (
    <div className="space-y-6">
      {isManager && (
        <div className="flex justify-end">
          <Button onClick={() => { setEditingId(null); setForm({ name: "", equipment_type: "tap_line", location: "", model: "", serial_number: "", notes: "" }); setShowForm(true); }}>
            Add Equipment
          </Button>
        </div>
      )}

      {/* Add/Edit Equipment Form */}
      {showForm && (
        <Card>
          <CardHeader title={editingId ? "Edit Equipment" : "Add Equipment"} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormGroup label="Name">
              <Input
                value={form.name}
                onChange={(v) => setForm({ ...form, name: v })}
                placeholder="e.g. Tap Line #1"
                required
              />
            </FormGroup>
            <FormGroup label="Type">
              <Select
                value={form.equipment_type}
                onChange={(v) => setForm({ ...form, equipment_type: v as "tap_line" | "slushie_machine" })}
              >
                <option value="tap_line">Tap Line</option>
                <option value="slushie_machine">Slushie Machine</option>
              </Select>
            </FormGroup>
            <FormGroup label="Location">
              <Input
                value={form.location}
                onChange={(v) => setForm({ ...form, location: v })}
                placeholder="e.g. Front bar, Walk-in cooler"
              />
            </FormGroup>
            <FormGroup label="Model">
              <Input
                value={form.model}
                onChange={(v) => setForm({ ...form, model: v })}
                placeholder="Model number"
              />
            </FormGroup>
            <FormGroup label="Serial Number">
              <Input
                value={form.serial_number}
                onChange={(v) => setForm({ ...form, serial_number: v })}
                placeholder="Serial number"
              />
            </FormGroup>
            <FormGroup label="Notes">
              <Input
                value={form.notes}
                onChange={(v) => setForm({ ...form, notes: v })}
                placeholder="Any notes"
              />
            </FormGroup>
          </div>
          <div className="flex gap-3 mt-4">
            <Button onClick={handleSubmitEquipment}>{editingId ? "Update" : "Save"}</Button>
            <Button variant="ghost" onClick={() => { setShowForm(false); setEditingId(null); }}>Cancel</Button>
          </div>
        </Card>
      )}

      {/* Tap Lines Section */}
      <div>
        <h3 className="text-lg font-bold mb-3" style={{ color: "var(--rbw-navy)" }}>Tap Lines</h3>
        {tapLines.length === 0 ? (
          <Card><EmptyState message="No tap lines added yet" icon="" /></Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {tapLines.map((eq) => (
              <EquipmentCard key={eq.id} equipment={eq} onClick={() => handleSelectEquipment(eq)} />
            ))}
          </div>
        )}
      </div>

      {/* Slushie Machines Section */}
      <div>
        <h3 className="text-lg font-bold mb-3" style={{ color: "var(--rbw-navy)" }}>Slushie Machines</h3>
        {slushieMachines.length === 0 ? (
          <Card><EmptyState message="No slushie machines added yet" icon="" /></Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {slushieMachines.map((eq) => (
              <EquipmentCard key={eq.id} equipment={eq} onClick={() => handleSelectEquipment(eq)} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function EquipmentCard({ equipment, onClick }: { equipment: Equipment; onClick: () => void }) {
  return (
    <button onClick={onClick} className="text-left w-full">
      <Card className="hover:shadow-md transition-shadow cursor-pointer">
        <div className="flex items-start justify-between mb-2">
          <div className="font-semibold" style={{ color: "var(--rbw-navy)" }}>
            {equipment.name}
          </div>
          {equipment.overdue_count > 0 && (
            <Badge variant="danger">{equipment.overdue_count} overdue</Badge>
          )}
        </div>
        <div className="text-xs space-y-1" style={{ color: "var(--rbw-gray-dark)" }}>
          {equipment.location && <div>Location: {equipment.location}</div>}
          <div>
            Last maintenance:{" "}
            {equipment.last_maintenance_at
              ? new Date(equipment.last_maintenance_at).toLocaleDateString()
              : "Never"}
          </div>
        </div>
      </Card>
    </button>
  );
}

// ============================================================
// LOG MAINTENANCE TAB
// ============================================================

function LogMaintenanceTab() {
  const [equipment, setEquipment] = useState<Equipment[]>([]);
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [selectedEquipmentId, setSelectedEquipmentId] = useState("");
  const [selectedScheduleId, setSelectedScheduleId] = useState("");
  const [selectedSchedule, setSelectedSchedule] = useState<Schedule | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const [logForm, setLogForm] = useState({
    work_performed: "",
    parts_used: "",
    parts_cost: "",
    issues_found: "",
    duration_minutes: "",
  });

  useEffect(() => {
    const loadEquipment = async () => {
      try {
        const [tapRes, slushieRes] = await Promise.all([
          api.get("/maintenance/equipment", { params: { type: "tap_line" } }),
          api.get("/maintenance/equipment", { params: { type: "slushie_machine" } }),
        ]);
        setEquipment([...tapRes.data, ...slushieRes.data]);
      } catch (err) {
        console.error("Failed to load equipment", err);
      }
    };
    loadEquipment();
  }, []);

  useEffect(() => {
    if (!selectedEquipmentId) {
      setSchedules([]);
      setSelectedScheduleId("");
      setSelectedSchedule(null);
      return;
    }
    const loadSchedules = async () => {
      try {
        const res = await api.get("/maintenance/schedules", {
          params: { equipment_id: selectedEquipmentId },
        });
        setSchedules(res.data);
      } catch (err) {
        console.error("Failed to load schedules", err);
      }
    };
    loadSchedules();
  }, [selectedEquipmentId]);

  useEffect(() => {
    if (selectedScheduleId) {
      const sched = schedules.find((s) => s.id === selectedScheduleId) || null;
      setSelectedSchedule(sched);
    } else {
      setSelectedSchedule(null);
    }
  }, [selectedScheduleId, schedules]);

  const handleSubmit = async () => {
    if (!selectedEquipmentId || !logForm.work_performed) return;
    setSubmitting(true);
    try {
      const taskName = selectedSchedule?.task_name || "General Maintenance";
      await api.post("/maintenance/log", {
        equipment_id: selectedEquipmentId,
        schedule_id: selectedScheduleId || undefined,
        task_name: taskName,
        work_performed: logForm.work_performed,
        parts_used: logForm.parts_used || undefined,
        parts_cost: logForm.parts_cost ? parseFloat(logForm.parts_cost) : undefined,
        issues_found: logForm.issues_found || undefined,
        duration_minutes: logForm.duration_minutes ? parseInt(logForm.duration_minutes) : undefined,
      });
      setSuccess(true);
      setLogForm({ work_performed: "", parts_used: "", parts_cost: "", issues_found: "", duration_minutes: "" });
      setSelectedEquipmentId("");
      setSelectedScheduleId("");
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error("Failed to log maintenance", err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl">
      {success && (
        <div
          className="rounded-xl p-4 mb-6"
          style={{ background: "#2D8A4E10", border: "1px solid #2D8A4E35", color: "var(--rbw-success)" }}
        >
          Maintenance logged successfully!
        </div>
      )}

      <Card>
        <CardHeader title="Log Completed Maintenance" />
        <div className="space-y-4">
          <FormGroup label="Equipment">
            <Select value={selectedEquipmentId} onChange={setSelectedEquipmentId}>
              <option value="">Select equipment...</option>
              {equipment.map((eq) => (
                <option key={eq.id} value={eq.id}>
                  {eq.name} ({eq.equipment_type === "tap_line" ? "Tap Line" : "Slushie Machine"})
                </option>
              ))}
            </Select>
          </FormGroup>

          {selectedEquipmentId && (
            <FormGroup label="Schedule / Task">
              <Select value={selectedScheduleId} onChange={setSelectedScheduleId}>
                <option value="">General maintenance (no schedule)</option>
                {schedules.map((sched) => (
                  <option key={sched.id} value={sched.id}>
                    {sched.task_name}
                    {sched.status === "overdue" ? " (OVERDUE)" : sched.status === "due_soon" ? " (Due Soon)" : ""}
                  </option>
                ))}
              </Select>
            </FormGroup>
          )}

          {/* Procedure Steps Reference */}
          {selectedSchedule && selectedSchedule.procedure_steps && selectedSchedule.procedure_steps.length > 0 && (
            <div
              className="rounded-xl p-4"
              style={{ background: "#8BB8E810", border: "1px solid #8BB8E830" }}
            >
              <div className="text-xs font-semibold mb-2" style={{ color: "var(--rbw-blue-dark)" }}>
                Procedure Checklist
              </div>
              <div className="space-y-1.5">
                {selectedSchedule.procedure_steps.map((step, i) => (
                  <label key={i} className="flex items-start gap-2 text-sm cursor-pointer" style={{ color: "var(--rbw-navy)" }}>
                    <input type="checkbox" className="mt-0.5 rounded" />
                    <span>{step}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <FormGroup label="Work Performed">
            <textarea
              value={logForm.work_performed}
              onChange={(e) => setLogForm({ ...logForm, work_performed: e.target.value })}
              placeholder="Describe the work that was done..."
              rows={3}
              required
              className="w-full px-4 py-2.5 rounded-xl outline-none transition-all"
              style={{
                border: "2px solid var(--rbw-border)",
                background: "var(--rbw-bg-card)",
                color: "var(--rbw-navy)",
              }}
              onFocus={(e) => (e.target.style.borderColor = "var(--rbw-blue)")}
              onBlur={(e) => (e.target.style.borderColor = "var(--rbw-border)")}
            />
          </FormGroup>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormGroup label="Parts Used">
              <Input
                value={logForm.parts_used}
                onChange={(v) => setLogForm({ ...logForm, parts_used: v })}
                placeholder="e.g. BLC line cleaner, faucet gasket"
              />
            </FormGroup>
            <FormGroup label="Parts Cost ($)">
              <Input
                value={logForm.parts_cost}
                onChange={(v) => setLogForm({ ...logForm, parts_cost: v })}
                type="number"
                placeholder="0.00"
                step="0.01"
                min="0"
              />
            </FormGroup>
            <FormGroup label="Issues Found">
              <Input
                value={logForm.issues_found}
                onChange={(v) => setLogForm({ ...logForm, issues_found: v })}
                placeholder="Any issues discovered during maintenance"
              />
            </FormGroup>
            <FormGroup label="Duration (minutes)">
              <Input
                value={logForm.duration_minutes}
                onChange={(v) => setLogForm({ ...logForm, duration_minutes: v })}
                type="number"
                placeholder="e.g. 30"
                min="1"
              />
            </FormGroup>
          </div>

          <div className="pt-2">
            <Button
              onClick={handleSubmit}
              disabled={submitting || !selectedEquipmentId || !logForm.work_performed}
              size="lg"
            >
              {submitting ? "Saving..." : "Log Maintenance"}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}

// ============================================================
// HISTORY TAB
// ============================================================

function HistoryTab() {
  const [history, setHistory] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadHistory = async () => {
      try {
        const res = await api.get("/maintenance/log");
        setHistory(res.data);
      } catch (err) {
        console.error("Failed to load maintenance history", err);
      } finally {
        setLoading(false);
      }
    };
    loadHistory();
  }, []);

  if (loading) {
    return <EmptyState message="Loading history..." icon="" />;
  }

  if (history.length === 0) {
    return <EmptyState message="No maintenance history yet" detail="Log your first maintenance entry to see it here" icon="" />;
  }

  return (
    <Table>
      <Thead>
        <Th>Date</Th>
        <Th>Equipment</Th>
        <Th>Task</Th>
        <Th>Performed By</Th>
        <Th>Duration</Th>
        <Th align="right">Parts Cost</Th>
        <Th>Issues Found</Th>
      </Thead>
      <tbody>
        {history.map((entry, i) => (
          <TRow key={entry.id} index={i}>
            <Td>{new Date(entry.created_at).toLocaleDateString()}</Td>
            <Td bold>{entry.equipment_name}</Td>
            <Td>{entry.task_name}</Td>
            <Td>{entry.performed_by || "-"}</Td>
            <Td>{entry.duration_minutes ? `${entry.duration_minutes} min` : "-"}</Td>
            <Td align="right">{entry.parts_cost ? `$${entry.parts_cost.toFixed(2)}` : "-"}</Td>
            <Td>{entry.issues_found || "-"}</Td>
          </TRow>
        ))}
      </tbody>
    </Table>
  );
}
